import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { countSeens } from "../containers/student/video/func";
import {
  exitPlayerFullscreen,
  loadVimeoPlayerApi,
  parseVimeoSource,
} from "../helpers/vimeoPlayer";
import { parseVideoQuestion, parseVideoQuizTime } from "../helpers/videoQuiz";
import { videoDataRequest } from "../modules/students-modules/videos/Actions";
import VideoQuickQuiz from "./VideoQuickQuiz";

const randomWatermarkSpot = () => ({
  top: 4 + Math.random() * 68,
  left: 4 + Math.random() * 48,
});

const WATERMARK_BREAK_MS = 30_000;

const randomWatermarkHoldMs = () => (1 + Math.floor(Math.random() * 5)) * 1000;

const watermarkText = (value) => (value == null ? "" : String(value).trim());

const VimeoEmbed = ({ videoUrl, data, trackProgress = true }) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const lastTimeRef = useRef(0);
  const playedDeltaRef = useRef(0);
  const hasCountedSeenRef = useRef(false);
  const quizShownRef = useRef(false);
  const quizOpenRef = useRef(false);
  const quizTimeRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;
  const [quizOpen, setQuizOpen] = useState(false);
  const [watermarkSpot, setWatermarkSpot] = useState(randomWatermarkSpot);
  const [watermarkVisible, setWatermarkVisible] = useState(false);

  const quizQuestion = parseVideoQuestion(data?.question);
  quizTimeRef.current = quizQuestion ? parseVideoQuizTime(data?.time) : null;
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const watermarkName = watermarkText(userData?.name);
  const watermarkPhone = watermarkText(userData?.phone);
  const showWatermark = Boolean(watermarkName || watermarkPhone);
  const videoData = useSelector((state) => state.chapterVideos.videoData);
  const [searchParams] = useSearchParams();
  const { id: routeId, video_id: routeVideoId } = useParams();

  const parsed = parseVimeoSource(videoUrl);
  const embedUrl = parsed?.embedUrl;

  useEffect(() => {
    if (!embedUrl || !showWatermark) return undefined;

    let timer;
    let cancelled = false;

    const hide = () => {
      if (cancelled) return;
      setWatermarkVisible(false);
      timer = window.setTimeout(show, WATERMARK_BREAK_MS);
    };

    const show = () => {
      if (cancelled) return;
      setWatermarkSpot(randomWatermarkSpot());
      setWatermarkVisible(true);
      timer = window.setTimeout(hide, randomWatermarkHoldMs());
    };

    show();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      setWatermarkVisible(false);
    };
  }, [embedUrl, showWatermark]);

  useEffect(() => {
    if (!embedUrl || !iframeRef.current || !trackProgress) return;

    let cancelled = false;
    let player;
    quizShownRef.current = false;
    quizOpenRef.current = false;
    setQuizOpen(false);

    const getVideoId = () =>
      dataRef.current?.id ||
      searchParams.get("video") ||
      routeVideoId ||
      routeId;

    const reportProgress = (currentTime, playedDelta) => {
      const videoId = getVideoId();
      if (!videoId || !userData?.id) return;
      if (!currentTime && !playedDelta) return;

      dispatch(
        videoDataRequest({
          total_played: playedDelta || 0,
          total_covered: currentTime || 0,
          video_id: videoId,
          user_id: userData.id,
        })
      );
    };

    const flushProgress = () => {
      const currentTime = lastTimeRef.current;
      const playedDelta = playedDeltaRef.current;
      playedDeltaRef.current = 0;
      reportProgress(currentTime, playedDelta);
    };

    loadVimeoPlayerApi()
      .then((Player) => {
        if (cancelled || !iframeRef.current) return;

        player = new Player(iframeRef.current);
        playerRef.current = player;

        player.on("play", () => {
          if (quizOpenRef.current) {
            player.pause();
            return;
          }
          isPlayingRef.current = true;
          if (!hasCountedSeenRef.current && dataRef.current) {
            hasCountedSeenRef.current = true;
            countSeens(dispatch, dataRef.current);
          }
        });

        player.on("pause", () => {
          isPlayingRef.current = false;
          flushProgress();
        });
        player.on("ended", () => {
          isPlayingRef.current = false;
          flushProgress();
        });

        player.on("timeupdate", (event) => {
          const seconds = event?.seconds || 0;
          if (isPlayingRef.current) {
            const delta = seconds - lastTimeRef.current;
            if (delta > 0 && delta < 2) {
              playedDeltaRef.current += delta;
            }
          }
          lastTimeRef.current = seconds;

          const cue = quizTimeRef.current;
          if (cue != null && !quizShownRef.current && seconds >= cue) {
            quizShownRef.current = true;
            quizOpenRef.current = true;
            isPlayingRef.current = false;
            exitPlayerFullscreen(player).finally(() => {
              player.pause();
              setQuizOpen(true);
            });
          }
        });
      })
      .catch(() => {});

    const onHidden = () => {
      if (document.visibilityState === "hidden") {
        isPlayingRef.current = false;
        flushProgress();
      }
    };

    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("beforeunload", flushProgress);

    return () => {
      cancelled = true;
      isPlayingRef.current = false;
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("beforeunload", flushProgress);
      if (player) {
        player.off("play");
        player.off("pause");
        player.off("ended");
        player.off("timeupdate");
        if (typeof player.destroy === "function") player.destroy();
      }
      playerRef.current = null;
    };
  }, [
    embedUrl,
    trackProgress,
    dispatch,
    userData?.id,
    searchParams,
    routeId,
    routeVideoId,
  ]);

  useEffect(() => {
    if (videoData?.views_count > 3) {
      window.location.reload();
    }
  }, [videoData]);

  const handleContinueQuiz = () => {
    quizOpenRef.current = false;
    setQuizOpen(false);
    playerRef.current?.play();
  };

  if (!parsed) {
    return <div dangerouslySetInnerHTML={{ __html: videoUrl }}></div>;
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingBottom: "56.25%",
      }}
    >
      <iframe
        ref={iframeRef}
        src={parsed.embedUrl}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title="Vimeo video"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
        }}
      />
      {showWatermark && watermarkVisible && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: `${watermarkSpot.top}%`,
            left: `${watermarkSpot.left}%`,
            zIndex: 2,
            maxWidth: "46%",
            pointerEvents: "none",
            userSelect: "none",
            color: "rgba(255, 255, 255, 0.92)",
            fontSize: "15px",
            fontWeight: 700,
            lineHeight: 1.35,
            textShadow:
              "0 1px 2px rgba(0, 0, 0, 0.9), 0 0 6px rgba(0, 0, 0, 0.65)",
            background: "rgba(0, 0, 0, 0.28)",
            padding: "2px 6px",
            borderRadius: 4,
          }}
        >
          {watermarkName}
          {watermarkName && watermarkPhone ? <br /> : null}
          {watermarkPhone}
        </div>
      )}
      <VideoQuickQuiz
        question={quizQuestion}
        isOpen={quizOpen}
        onContinue={handleContinueQuiz}
      />
    </div>
  );
};

export default VimeoEmbed;
